import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { addEvent, updateEvent, deleteEvent } from '../../../store/slices/calendarSlice';
import { CalendarEvent } from '../../../types/calendar';
import { createBlankEvent } from '../../../store/slices/calendarSlice';

interface EventModalProps {
  event: CalendarEvent | null;
  initialDate: string;
  onClose: () => void;
}

// Helper function to calculate max end repeat date (1 year from start date)
const getMaxEndRepeatDate = (startDate: string): string => {
  const start = new Date(startDate);
  const maxDate = new Date(start);
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  return maxDate.toISOString().split('T')[0];
};

// Helper function to generate recurring event instances
const generateRecurringEvents = (baseEvent: CalendarEvent): CalendarEvent[] => {
  if (!baseEvent.recurring || !baseEvent.frequency || !baseEvent.endRepeatDate) {
    return [baseEvent];
  }

  const events: CalendarEvent[] = [];
  const startDate = new Date(baseEvent.startDate);
  const endDate = new Date(baseEvent.endDate);
  const endRepeatDate = new Date(baseEvent.endRepeatDate);
  const eventDuration = endDate.getTime() - startDate.getTime();

  let currentDate = new Date(startDate);

  while (currentDate <= endRepeatDate) {
    const eventEndDate = new Date(currentDate.getTime() + eventDuration);

    events.push({
      ...baseEvent,
      id: `${baseEvent.id}-${currentDate.toISOString().split('T')[0]}`,
      startDate: currentDate.toISOString().split('T')[0],
      endDate: eventEndDate.toISOString().split('T')[0],
      updatedAt: new Date().toISOString()
    });

    // Increment based on frequency
    if (baseEvent.frequency === 'daily') {
      currentDate.setDate(currentDate.getDate() + 1);
    } else if (baseEvent.frequency === 'weekly') {
      currentDate.setDate(currentDate.getDate() + 7);
    } else if (baseEvent.frequency === 'monthly') {
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
  }

  return events;
};

const EventModal: React.FC<EventModalProps> = ({ event, initialDate, onClose }) => {
  const dispatch = useAppDispatch();
  const isEditing = !!event;

  const [formData, setFormData] = useState<CalendarEvent>(() => {
    if (event) return event;
    const blank = createBlankEvent();
    return { ...blank, startDate: initialDate, endDate: initialDate };
  });

  useEffect(() => {
    if (event) {
      setFormData(event);
    }
  }, [event]);

  const handleChange = (field: keyof CalendarEvent, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      alert('Please enter a title');
      return;
    }

    // Validate recurring event fields
    if (formData.recurring) {
      if (!formData.frequency) {
        alert('Please select a frequency for the recurring event');
        return;
      }
      if (!formData.endRepeatDate) {
        alert('Please select an end repeat date for the recurring event');
        return;
      }
    }

    if (isEditing) {
      dispatch(updateEvent(formData));
    } else {
      // Generate all recurring instances if it's a recurring event
      const eventsToAdd = generateRecurringEvents(formData);
      eventsToAdd.forEach(event => {
        dispatch(addEvent(event));
      });
    }

    onClose();
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      if (event) {
        dispatch(deleteEvent(event.id));
      }
      onClose();
    }
  };

  const colorOptions = [
    { value: '#2196F3', label: 'Blue' },
    { value: '#4CAF50', label: 'Green' },
    { value: '#FF9800', label: 'Orange' },
    { value: '#F44336', label: 'Red' },
    { value: '#9C27B0', label: 'Purple' },
    { value: '#00BCD4', label: 'Cyan' }
  ];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{isEditing ? 'Edit Event' : 'Create Event'}</h2>
          <button style={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => handleChange('title', e.target.value)}
              style={styles.input}
              placeholder="Event title"
              autoFocus
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Description</label>
            <textarea
              value={formData.description || ''}
              onChange={e => handleChange('description', e.target.value)}
              style={styles.textarea}
              placeholder="Event description"
              rows={3}
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Start Date</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={e => handleChange('startDate', e.target.value)}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>End Date</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={e => handleChange('endDate', e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.allDay}
                onChange={e => handleChange('allDay', e.target.checked)}
                style={styles.checkbox}
              />
              All-day event
            </label>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.recurring || false}
                onChange={e => handleChange('recurring', e.target.checked)}
                style={styles.checkbox}
              />
              Recurring
            </label>
          </div>

          {!formData.allDay && (
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Start Time</label>
                <input
                  type="time"
                  value={formData.startTime || ''}
                  onChange={e => handleChange('startTime', e.target.value)}
                  style={styles.input}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>End Time</label>
                <input
                  type="time"
                  value={formData.endTime || ''}
                  onChange={e => handleChange('endTime', e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>
          )}

          {formData.recurring && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>Frequency</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="frequency"
                      value="daily"
                      checked={formData.frequency === 'daily'}
                      onChange={e => handleChange('frequency', e.target.value)}
                      style={styles.radio}
                    />
                    Daily
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="frequency"
                      value="weekly"
                      checked={formData.frequency === 'weekly'}
                      onChange={e => handleChange('frequency', e.target.value)}
                      style={styles.radio}
                    />
                    Weekly
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      type="radio"
                      name="frequency"
                      value="monthly"
                      checked={formData.frequency === 'monthly'}
                      onChange={e => handleChange('frequency', e.target.value)}
                      style={styles.radio}
                    />
                    Monthly
                  </label>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>End Repeat Date</label>
                <input
                  type="date"
                  value={formData.endRepeatDate || ''}
                  onChange={e => handleChange('endRepeatDate', e.target.value)}
                  style={styles.input}
                  min={formData.startDate}
                  max={getMaxEndRepeatDate(formData.startDate)}
                />
              </div>
            </>
          )}

          <div style={styles.formGroup}>
            <label style={styles.label}>Color</label>
            <div style={styles.colorGrid}>
              {colorOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  style={{
                    ...styles.colorButton,
                    backgroundColor: option.value,
                    ...(formData.color === option.value ? styles.selectedColor : {})
                  }}
                  onClick={() => handleChange('color', option.value)}
                  title={option.label}
                />
              ))}
            </div>
          </div>

          <div style={styles.modalFooter}>
            {isEditing && (
              <button type="button" style={styles.deleteButton} onClick={handleDelete}>
                Delete
              </button>
            )}
            <div style={styles.rightButtons}>
              <button type="button" style={styles.cancelButton} onClick={onClose}>
                Cancel
              </button>
              <button type="submit" style={styles.submitButton}>
                {isEditing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0'
  },
  modalTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#333'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '32px',
    color: '#999',
    cursor: 'pointer',
    padding: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  form: {
    padding: '20px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#555'
  },
  input: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
    cursor: 'pointer'
  },
  checkbox: {
    marginRight: '8px',
    width: '18px',
    height: '18px',
    cursor: 'pointer'
  },
  radioGroup: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap'
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    fontWeight: '500',
    color: '#555',
    cursor: 'pointer'
  },
  radio: {
    marginRight: '6px',
    width: '16px',
    height: '16px',
    cursor: 'pointer'
  },
  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '8px'
  },
  colorButton: {
    width: '100%',
    paddingTop: '100%',
    border: '2px solid transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    position: 'relative'
  },
  selectedColor: {
    borderColor: '#333',
    boxShadow: '0 0 0 2px white, 0 0 0 4px #333'
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e0e0e0'
  },
  rightButtons: {
    display: 'flex',
    gap: '12px'
  },
  deleteButton: {
    padding: '10px 20px',
    backgroundColor: '#F44336',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: 'white',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};

export default EventModal;
